import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { useAuth, hasRole } from "../../context/AuthContext";

const Wrap = styled.section`
  background: var(--nd-black);
  min-height: 100vh;
  padding: 96px 32px 64px;
  color: var(--nd-text-primary);
  font-family: "Space Grotesk", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Eyebrow = styled.div`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const HeroNumber = styled.div`
  font-family: "Doto", "Space Mono", monospace;
  font-size: 120px;
  font-weight: 700;
  line-height: 0.9;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 80px;
  }
`;

const PageName = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0 0 8px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Purpose = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 480px;
  margin: 0 0 48px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 12px;
  column-gap: 24px;
  max-width: 480px;
  margin-bottom: 48px;
`;

const StatLabel = styled.div`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const StatValue = styled.div`
  font-family: "Space Mono", monospace;
  font-size: 13px;
  letter-spacing: 0.04em;
  color: var(--nd-text-primary);
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ color }) => color};
  margin-right: 8px;
  vertical-align: middle;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.a`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 14px 24px;
  border-radius: 999px;
  background: var(--nd-text-display);
  color: var(--nd-black) !important;
  border: none;
  text-decoration: none !important;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: opacity 200ms ease;
  &:hover { opacity: 0.85; color: var(--nd-black) !important; }
`;

const SecondaryBtn = styled(Link)`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 14px 24px;
  border-radius: 999px;
  background: transparent;
  color: var(--nd-text-primary) !important;
  border: 1px solid var(--nd-border-visible);
  text-decoration: none !important;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: border-color 200ms ease, color 200ms ease;
  &:hover {
    border-color: var(--nd-text-display);
    color: var(--nd-text-display) !important;
  }
`;

const SignInBtn = styled.button`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 14px 24px;
  border-radius: 999px;
  background: var(--nd-text-display);
  color: var(--nd-black);
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: opacity 200ms ease;
  &:hover { opacity: 0.85; }
`;

const ROLE_LABEL = {
  public: "PUBLIC (SIGNED OUT)",
  client: "CLIENT",
  staff: "STAFF",
  admin: "ADMIN",
};

const ROLE_DOT = {
  public: "var(--nd-text-disabled)",
  client: "var(--nd-warning)",
  staff: "var(--nd-warning)",
  admin: "var(--nd-success)",
};

/**
 * AccessGate — Nothing-Design empty state for role-gated pages.
 *
 * Props:
 *   pageName        — e.g. "Sales Pipeline"
 *   purpose         — short 1-2 line description of what the page does
 *   requiredRole    — "staff" | "admin"
 *   path            — current route, e.g. "/sales-leads"
 *   children        — rendered when access granted
 */
const AccessGate = ({ pageName, purpose, requiredRole, path, children }) => {
  const { user, role, loading, signIn } = useAuth();

  if (loading) {
    return (
      <Wrap>
        <Eyebrow>[ LOADING... ]</Eyebrow>
      </Wrap>
    );
  }

  const currentRole = role || "public";
  if (hasRole(currentRole, requiredRole)) return children;

  const signedIn = Boolean(user);
  const heroCode = signedIn ? "403" : "401";
  const eyebrow = signedIn ? "[ INSUFFICIENT PRIVILEGES ]" : "[ AUTHENTICATION REQUIRED ]";

  return (
    <Wrap>
      <Eyebrow>{eyebrow}</Eyebrow>
      <HeroNumber aria-label={`Error ${heroCode}`}>{heroCode}</HeroNumber>
      <PageName>{pageName}</PageName>
      <Purpose>{purpose}</Purpose>

      <Stats>
        <StatLabel>Your role</StatLabel>
        <StatValue>
          <StatusDot color={ROLE_DOT[currentRole]} aria-hidden="true" />
          {ROLE_LABEL[currentRole] || currentRole.toUpperCase()}
        </StatValue>

        <StatLabel>Required</StatLabel>
        <StatValue>
          <StatusDot color={ROLE_DOT[requiredRole]} aria-hidden="true" />
          {ROLE_LABEL[requiredRole] || requiredRole.toUpperCase()}
        </StatValue>

        <StatLabel>Path</StatLabel>
        <StatValue>{path}</StatValue>

        {signedIn && (
          <>
            <StatLabel>Account</StatLabel>
            <StatValue>{user.email}</StatValue>
          </>
        )}
      </Stats>

      <Actions>
        {!signedIn && (
          <SignInBtn onClick={signIn}>Sign in with Google ↗</SignInBtn>
        )}
        {signedIn && (
          <PrimaryBtn
            href={`mailto:hello@dioscuro.com?subject=Access%20request%3A%20${encodeURIComponent(
              pageName
            )}&body=${encodeURIComponent(
              `Hi,\n\nI'd like access to the ${pageName} page (${path}).\n\nMy account: ${user.email}\nCurrent role: ${currentRole}\nRole required: ${requiredRole}\n\nThanks.`
            )}`}
          >
            Request access →
          </PrimaryBtn>
        )}
        <SecondaryBtn to="/tools">← Back to tools</SecondaryBtn>
      </Actions>
    </Wrap>
  );
};

export default AccessGate;
